import React, { useState, useRef, useCallback } from "react";
import styles from "./ProductPreview.module.scss";

type VideoPreview = {
    title: string;
    isDemo?: boolean;
    fileUrl?: string | null;
};

interface Props {
    title: string;
    author: string;
    price: string;
    discount: string;
    images: (string | null)[];
    description: string;
    videos: VideoPreview[];
}

export const ProductPreview: React.FC<Props> = ({
                                                    title,
                                                    author,
                                                    price,
                                                    discount,
                                                    images,
                                                    description,
                                                    videos,
                                                }) => {
    const [playingVideo, setPlayingVideo] = useState<number | null>(null);
    const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());

    const parseNumber = (s: string) => {
        if (!s) return 0;
        const normalized = s.replace(/\s+/g, "").replace(",", ".");
        const n = parseFloat(normalized);
        return Number.isFinite(n) ? n : 0;
    };

    const base = parseNumber(price);
    const disc = parseNumber(discount);
    const hasDiscount = disc > 0 && base > 0;
    const finalPrice = hasDiscount ? Math.max(0, base - disc) : base;

    const fmt = (n: number) =>
        isNaN(n) ? "" : new Intl.NumberFormat("ru-RU").format(n) + " ₽";

    const hasImages = images.some(Boolean);
    const hasDiscountError = disc > base;

    const handleVideoPlay = useCallback((index: number) => {
        // Останавливаем все другие видео
        videoRefs.current.forEach((video, i) => {
            if (video && i !== index) {
                video.pause();
                video.currentTime = 0;
            }
        });
        setPlayingVideo(index);
    }, []);

    const handleVideoPause = useCallback((index: number) => {
        setPlayingVideo(null);
    }, []);

    const setVideoRef = useCallback((index: number) => (el: HTMLVideoElement | null) => {
        if (el) {
            videoRefs.current.set(index, el);
        } else {
            videoRefs.current.delete(index);
        }
    }, []);

    return (
        <div className={styles.preview}>
            <div className={styles.infoBlock}>
                <div className={styles.sectionTitle}>Информация о товаре</div>

                <div className={styles.field}>
                    <div className={styles.fieldLabel}>Название курса</div>
                    <div className={styles.fieldValueBig}>
                        {title || <span className={styles.missing}>Не заполнено</span>}
                    </div>
                </div>

                <div className={styles.field}>
                    <div className={styles.fieldLabel}>Автор курса</div>
                    <div className={styles.fieldValueBig}>
                        {author || <span className={styles.missing}>Не заполнено</span>}
                    </div>
                </div>

                <div className={styles.field}>
                    <div className={styles.fieldLabel}>Цена</div>
                    <div className={styles.priceBlock}>
                        {hasDiscount ? (
                            <>
                                <div className={styles.priceDiscounted}>{fmt(finalPrice)}</div>
                                <div className={styles.priceOriginal}>{fmt(base)}</div>
                            </>
                        ) : (
                            <div className={styles.priceNormal}>
                                {base > 0 ? fmt(base) : <span className={styles.missing}>Не заполнено</span>}
                            </div>
                        )}
                    </div>
                    {hasDiscountError && (
                        <div className={styles.errorMessage}>⚠️ Сумма скидки больше цены!</div>
                    )}
                </div>

                <div className={styles.imagesTitle}>Изображения</div>
                {hasImages ? (
                    <div className={styles.imagesRow}>
                        {images.map((src, idx) =>
                            src ? (
                                <div key={idx} className={styles.smallWrapper}>
                                    <img src={src} alt={`img-${idx}`} className={styles.smallImage} />
                                </div>
                            ) : null
                        )}
                    </div>
                ) : (
                    <div className={styles.missing}>Фото не загружено</div>
                )}

                <div className={styles.sectionTitle}>Описание товара</div>
                <div className={styles.description}>
                    {description || <span className={styles.missing}>Не заполнено</span>}
                </div>

                <div className={styles.sectionTitle}>Видео</div>
                <div className={styles.videosList}>
                    {videos.length ? (
                        videos.map((v, i) => (
                            <div key={i} className={styles.videoRow}>
                                <div className={styles.videoContainer}>
                                    {v.fileUrl ? (
                                        <video
                                            ref={setVideoRef(i)}
                                            src={v.fileUrl}
                                            className={styles.videoPlayer}
                                            controls
                                            onPlay={() => handleVideoPlay(i)}
                                            onPause={() => handleVideoPause(i)}
                                            preload="metadata"
                                        />
                                    ) : (
                                        <div className={styles.videoPlaceholder}>
                                            <div className={styles.noVideoText}>Видео не загружено</div>
                                        </div>
                                    )}
                                </div>
                                <div className={styles.videoMeta}>
                                    <div className={styles.videoTitle}>
                                        {v.title || <span className={styles.missing}>Не добавлено</span>}
                                    </div>
                                    {v.isDemo && <div className={styles.demoFlag}>Демо</div>}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className={styles.missing}>Видео не добавлены</div>
                    )}
                </div>
            </div>
        </div>
    );
};